import { useRef, useState, type KeyboardEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  Activity,
  Ban,
  Building2,
  Clock,
  KeyRound,
  Mail,
  Landmark,
  RefreshCw,
  ShieldCheck,
  Tag,
  Trash2,
  Users,
  UsersRound,
  X,
} from 'lucide-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  DEFAULT_ORGANIZATION_SUBSCRIPTION_TIER,
  ENTERPRISE_IDENTITY_FEATURE,
  ORGANIZATION_SUBSCRIPTION_TIERS,
  SUBSCRIPTION_TIER_LABELS,
  effectiveSubscriptionTier,
  hasEnterpriseIdentityTier,
  isSubscriptionActive,
} from '@reflex/shared';
import type {
  AccountStatus,
  AdminEnterpriseIdentityStatus,
  AdminOrganizationBillingEntity,
  AdminOrganizationBillingEntityMoveResult,
  BillingEntity,
  Organization,
  OrganizationSubscriptionTier,
  Role,
  Team,
  User,
} from '@reflex/shared';
import { Badge } from '@reflex/ui/components/ui/badge';
import { Button } from '@reflex/ui/components/ui/button';
import { Input } from '@reflex/ui/components/ui/input';
import { Label } from '@reflex/ui/components/ui/label';
import { Switch } from '@reflex/ui/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@reflex/ui/components/ui/select';
import { SectionCard } from '@reflex/ui/components/shared/SectionCard';
import { StatGrid, type StatGridItem } from '@reflex/ui/components/shared/StatGrid';
import { PageDescription, SectionLabel } from '@reflex/ui/components/layout/Typography';
import { SectionNav } from '@reflex/ui/components/layout/SectionNav';
import { DataTable, type DataTableColumn } from '@/components/shared/DataTable';
import { ConfirmDialog } from '@reflex/ui/components/shared/ConfirmDialog';
import { AdminSubPageLayout } from '@reflex/ui/components/layout/AdminSubPageLayout';
import { UserAvatar } from '@/components/shared/UserAvatar';
import { EmptyState } from '@reflex/ui/components/shared/EmptyState';
import { LoadingState } from '@reflex/ui/components/shared/LoadingState';
import { AsyncContent } from '@/components/shared/AsyncContent';
import { MemberPicker } from '@/components/members/MemberPicker';
import { OrganizationInvites } from '@/components/members/OrganizationInvites';
import { MemberRolesPopover } from '@/components/members/MemberRolesPopover';
import { useAuth } from '@/hooks/auth/useAuth';
import { useRefreshSessionIfSelf } from '@/hooks/auth/useSessionRefresh';
import { ApiError, api } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import { formatDateLong, formatDateOnly, formatRelativeTime } from '@reflex/ui/lib/format';
import { getUserDisplayName } from '@/lib/agent-utils';
import { ROUTES } from '@/lib/routes';

interface MemberRow {
  userId: string;
  memberId: string;
  roles: Role[];
  user: User | undefined;
}

function buildTeamColumns(orgSlug: string): DataTableColumn<Team>[] {
  return [
    {
      header: 'Name',
      cell: (team) => (
        <Link
          to={ROUTES.org(orgSlug).team(team.id)}
          data-testid={`admin-organization-team-row-${team.id}`}
          aria-label={`View team ${team.name}`}
          className="block min-w-0 transition-opacity hover:opacity-80"
        >
          <p className="truncate font-medium">{team.name}</p>
          <p className="truncate text-xs text-muted-foreground">{team.slug}</p>
        </Link>
      ),
    },
    {
      header: 'Description',
      cell: (team) => team.description ?? <span className="italic text-muted-foreground">—</span>,
      cellClassName: 'text-muted-foreground',
    },
    {
      header: 'Created',
      cell: (team) => formatDateOnly(team.createdAt),
      cellClassName: 'text-xs text-muted-foreground',
    },
  ];
}

interface BuildMemberColumnsArgs {
  availableRoles: Role[];
  rolesPending: boolean;
  removePending: boolean;
  onToggleRole: (userId: string, roleId: string, next: boolean) => void;
  onRemove: (row: MemberRow) => void;
}

function buildMemberColumns({
  availableRoles,
  rolesPending,
  removePending,
  onToggleRole,
  onRemove,
}: BuildMemberColumnsArgs): DataTableColumn<MemberRow>[] {
  return [
    {
      header: 'Member',
      cell: (row) => {
        const display = row.user ? getUserDisplayName(row.user) : row.userId;
        return (
          <Link
            to={ROUTES.admin.user(row.userId)}
            data-testid={`admin-organization-member-row-${row.userId}`}
            aria-label={`View user ${display}`}
            className="flex items-center gap-2.5 transition-opacity hover:opacity-80"
          >
            <UserAvatar userName={display} avatarUrl={row.user?.avatarUrl ?? undefined} size="md" />
            <div className="min-w-0">
              <p className="truncate font-medium">{display}</p>
              {row.user?.login && (
                <p className="truncate text-xs text-muted-foreground">@{row.user.login}</p>
              )}
            </div>
          </Link>
        );
      },
    },
    {
      header: 'Roles',
      cell: (row) => (
        <MemberRolesPopover
          availableRoles={availableRoles}
          assignedRoles={row.roles}
          memberKey={row.userId}
          pending={rolesPending}
          onToggle={(roleId, next) => onToggleRole(row.userId, roleId, next)}
        />
      ),
    },
    {
      header: 'Member ID',
      cell: (row) => row.memberId,
      cellClassName: 'font-mono text-xs text-muted-foreground',
    },
    {
      header: '',
      srHeader: 'Actions',
      cell: (row) => {
        const display = row.user ? getUserDisplayName(row.user) : row.userId;
        return (
          <ConfirmDialog
            title="Remove member"
            description={
              <>
                Remove <strong>{display}</strong> from this organization? Their role assignments
                will be revoked. They can be added back at any time.
              </>
            }
            confirmLabel="Remove"
            onConfirm={() => onRemove(row)}
            disabled={removePending}
            trigger={
              <Button
                variant="ghost"
                size="icon-sm"
                data-testid={`admin-organization-member-remove-${row.userId}`}
                aria-label={`Remove ${display}`}
                disabled={removePending}
              >
                <Trash2 className="icon-sm" />
              </Button>
            }
          />
        );
      },
      cellClassName: 'text-right',
    },
  ];
}

interface IdentitySectionProps {
  organization: Organization;
  creator: User | undefined;
}

function IdentitySection({ organization, creator }: IdentitySectionProps) {
  return (
    <SectionCard title="Identity" icon={<Building2 />} contentClassName="space-y-4">
      <div className="flex flex-col items-start gap-4 sm:flex-row sm:items-center">
        <UserAvatar
          userName={organization.name}
          avatarUrl={organization.avatarUrl ?? undefined}
          size="md"
          className="shrink-0"
        />
        <div className="min-w-0 flex-1 space-y-1">
          <p className="truncate text-base font-medium">{organization.name}</p>
          <p className="truncate font-mono text-xs text-muted-foreground">{organization.slug}</p>
        </div>
      </div>

      <dl className="grid grid-cols-1 gap-x-6 gap-y-3 text-sm sm:grid-cols-2">
        <div>
          <SectionLabel as="dt">Organization ID</SectionLabel>
          <dd className="mt-0.5 truncate font-mono text-xs">{organization.id}</dd>
        </div>
        <div>
          <SectionLabel as="dt">Created</SectionLabel>
          <dd className="mt-0.5">{formatDateLong(organization.createdAt)}</dd>
        </div>
        <div>
          <SectionLabel as="dt">Type</SectionLabel>
          <dd className="mt-0.5">
            {organization.isPersonal ? (
              <Badge variant="outline">Personal</Badge>
            ) : (
              <Badge variant="secondary">Shared</Badge>
            )}
          </dd>
        </div>
        <div>
          <SectionLabel as="dt">Created by</SectionLabel>
          <dd className="mt-0.5" data-testid="admin-organization-created-by">
            {organization.createdByUserId ? (
              <Link
                to={ROUTES.admin.user(organization.createdByUserId)}
                className="underline-offset-2 hover:underline"
              >
                {creator ? getUserDisplayName(creator) : organization.createdByUserId}
              </Link>
            ) : (
              <span className="italic text-muted-foreground">System</span>
            )}
          </dd>
        </div>
        <div>
          <SectionLabel as="dt">Support email</SectionLabel>
          <dd className="mt-0.5" data-testid="admin-organization-support-email">
            {organization.supportEmail ? (
              <a
                href={`mailto:${organization.supportEmail}`}
                className="underline-offset-2 hover:underline"
              >
                {organization.supportEmail}
              </a>
            ) : (
              <span className="italic text-muted-foreground">Not set</span>
            )}
          </dd>
        </div>
      </dl>
    </SectionCard>
  );
}

interface MembersSectionProps {
  members: MemberRow[] | undefined;
  isLoading: boolean;
  isError: boolean;
  addableUsers: User[];
  onAddMember: (userId: string) => void;
  isAdding: boolean;
  availableRoles: Role[];
  rolesPending: boolean;
  removePending: boolean;
  onToggleRole: (userId: string, roleId: string, next: boolean) => void;
  onRemove: (row: MemberRow) => void;
}

function MembersSection({
  members,
  isLoading,
  isError,
  addableUsers,
  onAddMember,
  isAdding,
  availableRoles,
  rolesPending,
  removePending,
  onToggleRole,
  onRemove,
}: MembersSectionProps) {
  const count = members?.length ?? 0;
  const columns = buildMemberColumns({
    availableRoles,
    rolesPending,
    removePending,
    onToggleRole,
    onRemove,
  });
  return (
    <SectionCard
      title="Members"
      description={isLoading ? 'Loading…' : `${count} member${count !== 1 ? 's' : ''}`}
      icon={<Users />}
      action={
        <MemberPicker
          users={addableUsers}
          onSelect={onAddMember}
          disabled={isAdding}
          triggerLabel="Add member"
          ariaLabel="Add organization member"
          searchPlaceholder="Search users..."
        />
      }
      contentClassName="pt-0"
    >
      <AsyncContent isLoading={isLoading} isError={isError} errorMessage="Failed to load members.">
        {count === 0 ? (
          <EmptyState
            variant="bordered"
            icon={<Users className="h-8 w-8" />}
            title="No members yet"
            description="This organization does not have any members."
          />
        ) : (
          <DataTable<MemberRow> data={members!} columns={columns} rowKey={(row) => row.memberId} />
        )}
      </AsyncContent>
    </SectionCard>
  );
}

/**
 * Email invites for an arbitrary tenant. Unlike "Add member", which can only
 * pick from users who already exist, an invite reserves an email address: the
 * invitee gets a redemption link and joins this organization the first time
 * they sign in with that address.
 */
function InvitesSection({ organizationId }: { organizationId: string }) {
  return (
    <SectionCard
      title="Invites"
      description="Invite people by email, including people who have not signed up yet."
      icon={<Mail />}
      data-testid="admin-organization-invites"
      contentClassName="pt-0"
    >
      {/*
       * Revoke is always on here. This page is behind `RequirePlatformAdmin`
       * and every invite route resolves the org from the path, where
       * `requirePermOnPathOrg` returns early for platform admins — so the
       * caller holds `members:manage` on this org by definition, whether or
       * not they are a member of it. Passed explicitly rather than defaulted
       * so that reasoning lives at the call site it applies to.
       */}
      <OrganizationInvites organizationId={organizationId} canRevoke />
    </SectionCard>
  );
}

interface CapabilitiesSectionProps {
  organization: Organization;
  /**
   * When false the section renders read-only chips with no editor.
   * Platform admins get the editor; org-admins still see the granted set
   * (capabilities are public per-org metadata, not a secret).
   */
  canEdit: boolean;
}

function CapabilitiesSection({ organization, canEdit }: CapabilitiesSectionProps) {
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState('');
  const [draftError, setDraftError] = useState<string | null>(null);

  const updateMutation = useMutation({
    mutationFn: ({ capability, enabled }: { capability: string; enabled: boolean }) =>
      api.setAdminOrganizationCapability(organization.id, capability, enabled),
    onSuccess: () => {
      toast.success('Capabilities updated');
      void queryClient.invalidateQueries({
        queryKey: queryKeys.adminOrganization(organization.id),
      });
    },
    onError: (err) => {
      // 403 here means the caller dropped platform-admin between page load
      // and the PATCH; we surface the structured message rather than the
      // raw fetch error so the operator can react.
      const description =
        err instanceof ApiError && err.status === 403
          ? 'Capability management is restricted to platform admins.'
          : err instanceof Error
            ? err.message
            : undefined;
      toast.error('Failed to update capabilities', { description });
    },
  });

  // Wire schema models `capabilities` as optional so legacy fixtures
  // round-trip; collapse undefined to an empty array here so the editor
  // doesn't have to special-case both states.
  const value = organization.capabilities ?? [];
  const busy = updateMutation.isPending;

  function tryAdd(): void {
    const candidate = draft.trim();
    if (candidate.length === 0) return;
    // Enterprise identity is not a capability slug: entitlement follows the
    // subscription tier, so a slug typed here would grant nothing.
    if (candidate.toLowerCase() === ENTERPRISE_IDENTITY_FEATURE) {
      setDraftError('Enterprise identity is included with the Enterprise subscription tier.');
      return;
    }
    // Free-form slugs — only constraint is non-empty + unique. The host
    // doesn't enforce a format because capabilities are conceptually
    // open-ended labels that plugins reference by string.
    if (value.includes(candidate)) {
      setDraftError(`"${candidate}" is already granted`);
      return;
    }
    updateMutation.mutate({ capability: candidate, enabled: true });
    setDraft('');
    setDraftError(null);
  }

  function handleKeyDown(e: KeyboardEvent<HTMLInputElement>): void {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      tryAdd();
    } else if (e.key === 'Backspace' && draft.length === 0 && value.length > 0 && canEdit) {
      e.preventDefault();
      updateMutation.mutate({ capability: value[value.length - 1]!, enabled: false });
    }
  }

  function handleRemove(slug: string): void {
    updateMutation.mutate({ capability: slug, enabled: false });
  }

  return (
    <SectionCard
      title="Capabilities"
      description={
        canEdit
          ? 'Grant free-form capability slugs to unlock product and plugin features. Platform admins only.'
          : 'Capabilities granted to this organization. Platform admins manage this list.'
      }
      icon={<KeyRound />}
      contentClassName="pt-0"
    >
      <div className="space-y-2" data-testid="admin-organization-capabilities">
        {value.length > 0 ? (
          <div
            className="flex flex-wrap gap-1.5"
            data-testid="admin-organization-capabilities-chips"
          >
            {value.map((slug) => (
              <Badge
                key={slug}
                variant="outline"
                className="gap-1.5 bg-muted/40 py-1 pl-2.5 pr-1 leading-none"
                data-testid={`admin-organization-capabilities-chip-${slug}`}
              >
                <span className="font-mono">{slug}</span>
                {canEdit && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon-xs"
                    onClick={() => handleRemove(slug)}
                    aria-label={`Remove capability ${slug}`}
                    className="-mr-0.5 rounded-full"
                    disabled={busy}
                  >
                    <X className="h-3 w-3" />
                  </Button>
                )}
              </Badge>
            ))}
          </div>
        ) : (
          <p className="text-sm italic text-muted-foreground">No capabilities granted yet.</p>
        )}
        {canEdit && (
          <div className="flex items-start gap-2">
            <div className="min-w-0 flex-1">
              <Input
                value={draft}
                onChange={(e) => {
                  setDraft(e.target.value);
                  if (draftError) setDraftError(null);
                }}
                onKeyDown={handleKeyDown}
                placeholder="e.g. runloop-plugins"
                aria-label="Add capability"
                aria-invalid={draftError ? true : undefined}
                disabled={busy}
                autoComplete="off"
                spellCheck={false}
                data-testid="admin-organization-capabilities-input"
              />
              {draftError && (
                <p role="alert" className="mt-1 text-xs text-destructive">
                  {draftError}
                </p>
              )}
            </div>
            <Button
              type="button"
              variant="secondary"
              onClick={tryAdd}
              disabled={draft.trim().length === 0 || busy}
              data-testid="admin-organization-capabilities-add"
            >
              Add
            </Button>
          </div>
        )}
      </div>
    </SectionCard>
  );
}

const SUBSCRIPTION_TIER_CARD_DESCRIPTION =
  'The tier this organization is on. Enterprise includes enterprise identity. On any other tier those features stop working, but their settings are kept.';

/** Human labels for the features an org has configured, in card order. */
function configuredFeatureLabels(status: AdminEnterpriseIdentityStatus | undefined): string[] {
  if (!status) return [];
  return [
    status.configuredFeatures.identityProvider ? 'Identity provider' : null,
    status.configuredFeatures.scim ? 'SCIM' : null,
    status.configuredFeatures.oktaSync ? 'Okta sync' : null,
    status.configuredFeatures.idJag ? 'ID-JAG' : null,
  ].filter((feature): feature is string => feature !== null);
}

/**
 * What we know about an org's commercial owner. Deliberately not
 * `AdminOrganizationBillingEntity | null`: "the read has not answered" and
 * "the organization does not exist" are different facts. Every existing
 * organization has exactly one company owner.
 *
 * `hidden` is a viewer who may not read the owner at all: the answer is
 * unknown to them, never "standalone".
 */
type OwnerState =
  | { status: 'loading' }
  | { status: 'error' }
  | { status: 'hidden' }
  | { status: 'ready'; owner: AdminOrganizationBillingEntity };

/** The billing entity governing this org, or `undefined` when we do not know. */
function governingBillingEntity(state: OwnerState): BillingEntity | undefined {
  return state.status === 'ready' ? state.owner.billingEntity : undefined;
}

/** Server refusal code for a move that would switch a required single sign-on off. */
const SSO_RESET_ACKNOWLEDGEMENT_REQUIRED = 'sso_reset_acknowledgement_required';

function isSsoResetRefusal(err: unknown): boolean {
  if (!(err instanceof ApiError) || err.status !== 409) return false;
  return (
    err.code === SSO_RESET_ACKNOWLEDGEMENT_REQUIRED ||
    err.body.error === SSO_RESET_ACKNOWLEDGEMENT_REQUIRED ||
    err.message.includes(SSO_RESET_ACKNOWLEDGEMENT_REQUIRED)
  );
}

/**
 * Plain-language consequences of a company move for the success toast: what
 * happened to the subscription window, and whether single sign-on was
 * switched off. `undefined` when nothing but the owner changed.
 */
function describeMoveConsequences(
  result: AdminOrganizationBillingEntityMoveResult,
): string | undefined {
  const parts: string[] = [];
  const before = result.subscriptionBefore;
  const after = result.subscriptionAfter;
  const describe = (window: typeof before): string =>
    window
      ? `${window.type} until ${formatDateOnly(window.endsAt)}`
      : 'unlimited access, no subscription window';
  if (before?.endsAt !== after?.endsAt || before?.type !== after?.type) {
    parts.push(`Subscription window changed from ${describe(before)} to ${describe(after)}.`);
  }
  if (result.clearedSsoFlags.length > 0) {
    parts.push('Single sign-on was switched off for this organization.');
  }
  return parts.length > 0 ? parts.join(' ') : undefined;
}

interface BillingEntitySectionProps {
  organization: Organization;
  /** The org's commercial owner. */
  ownerState: OwnerState;
  /** Refetch the owner read after it failed. */
  onRetryOwner: () => void;
  /** Platform admins can move and re-tier; everyone else sees the state. */
  canEdit: boolean;
}

/**
 * Card that binds the org to its billing entity — the commercial owner that can hold
 * several organizations (finance, engineering) under one contract. While
 * attached, the billing entity's subscription tier governs the org (the tier card
 * below goes read-only), so moving and re-tiering the billing entity
 * are all entitlement-changing acts and stay platform-admin only. Membership
 * and org scoping are untouched by any of this: the billing entity carries
 * governance, never data access.
 *
 * The owner comes from `GET /admin/organizations/:id/billing-entity`, the only read
 * that reports it — the organization resource carries no billing entity, so an org's
 * own members never learn who owns them commercially.
 */
function BillingEntitySection({
  organization,
  ownerState,
  onRetryOwner,
  canEdit,
}: BillingEntitySectionProps) {
  const queryClient = useQueryClient();
  const [pendingBillingEntityTier, setPendingBillingEntityTier] =
    useState<OrganizationSubscriptionTier | null>(null);
  // A company move is a written, audited act: it re-homes the subscription
  // window and can switch single sign-on off, so the picker only stages the
  // target and the dialog collects the reason (and, when the server asks for
  // it, the acknowledgement) before anything is written.
  const [pendingMove, setPendingMove] = useState<{
    billingEntityId: string;
    reason: string;
    ssoResetRequired: boolean;
    acknowledgeSsoReset: boolean;
  } | null>(null);
  const [pendingDomainChange, setPendingDomainChange] = useState<{
    domain: string;
    action: 'remove' | 'reassign';
    billingEntityId?: string;
    reason: string;
  } | null>(null);
  // Radix closes the alert dialog on the confirm click before the mutation
  // has rendered as pending, so the close handler consults this ref instead
  // of `isPending` to keep the dialog open while the move is in flight.
  const moveInFlight = useRef(false);
  const billingEntity = governingBillingEntity(ownerState);
  const ownedOrganizations = ownerState.status === 'ready' ? ownerState.owner.organizations : [];
  // Sibling orgs the change would also hit; the org on screen is not news.
  const siblingSlugs = ownedOrganizations
    .filter((owned) => owned.id !== organization.id)
    .map((owned) => owned.slug);

  const billingEntitiesQuery = useQuery({
    queryKey: queryKeys.adminBillingEntities,
    queryFn: () => api.listAdminBillingEntities(),
    enabled: canEdit,
  });
  const companyDomainsQuery = useQuery({
    queryKey: ['admin', 'billing-entities', billingEntity?.id, 'email-domains'],
    queryFn: () => api.listAdminCompanyDomainClaims(billingEntity?.id ?? ''),
    enabled: canEdit && billingEntity !== undefined,
  });

  // `adminOrganization(id)` is a prefix of every per-org admin key, so this one
  // call also invalidates the owner read and the enterprise-identity status.
  function invalidate(): void {
    void queryClient.invalidateQueries({ queryKey: queryKeys.adminOrganization(organization.id) });
    void queryClient.invalidateQueries({ queryKey: queryKeys.adminBillingEntities });
    void queryClient.invalidateQueries({ queryKey: queryKeys.adminOrganizations });
    void queryClient.invalidateQueries({
      queryKey: ['admin', 'billing-entities', billingEntity?.id, 'email-domains'],
    });
  }

  const attachMutation = useMutation({
    mutationFn: (input: {
      billingEntityId: string;
      reason: string;
      acknowledgeSsoReset: boolean;
    }) => api.setAdminOrganizationBillingEntity(organization.id, input),
    onSuccess: (result) => {
      setPendingMove(null);
      toast.success('Organization moved to its new company', {
        description: describeMoveConsequences(result),
      });
      invalidate();
    },
    onError: (err) => {
      // The server refuses to switch a required single sign-on off without an
      // explicit acknowledgement. Keep the dialog open and ask for it.
      if (isSsoResetRefusal(err)) {
        setPendingMove((current) => (current ? { ...current, ssoResetRequired: true } : current));
        return;
      }
      setPendingMove(null);
      toast.error('Could not change the owner', {
        description: err instanceof Error ? err.message : undefined,
      });
    },
    onSettled: () => {
      moveInFlight.current = false;
    },
  });

  const billingEntityTierMutation = useMutation({
    mutationFn: (next: OrganizationSubscriptionTier) =>
      api.updateAdminBillingEntity(billingEntity?.id ?? '', { subscriptionTier: next }),
    onSuccess: (updated) => {
      toast.success(
        `Subscription tier changed to ${SUBSCRIPTION_TIER_LABELS[updated.subscriptionTier]}`,
      );
      invalidate();
    },
    onError: (err) => {
      toast.error('Could not change the subscription tier', {
        description: err instanceof Error ? err.message : undefined,
      });
    },
  });

  const removeDomainMutation = useMutation({
    mutationFn: (input: { domain: string; reason: string }) =>
      api.removeAdminCompanyDomainClaim(input.domain, input.reason),
    onSuccess: () => {
      setPendingDomainChange(null);
      toast.success('Company email domain removed');
      invalidate();
    },
    onError: (error) =>
      toast.error('Could not remove the company email domain', {
        description: error instanceof Error ? error.message : undefined,
      }),
  });

  const reassignDomainMutation = useMutation({
    mutationFn: (input: { domain: string; billingEntityId: string; reason: string }) =>
      api.reassignAdminCompanyDomainClaim(input.domain, input.billingEntityId, input.reason),
    onSuccess: () => {
      setPendingDomainChange(null);
      toast.success('Company email domain moved');
      invalidate();
    },
    onError: (error) =>
      toast.error('Could not move the company email domain', {
        description: error instanceof Error ? error.message : undefined,
      }),
  });

  function handleBillingEntityTierSelect(next: OrganizationSubscriptionTier): void {
    if (
      billingEntity &&
      hasEnterpriseIdentityTier(billingEntity.subscriptionTier) &&
      !hasEnterpriseIdentityTier(next)
    ) {
      setPendingBillingEntityTier(next);
      return;
    }
    billingEntityTierMutation.mutate(next);
  }

  const busy = attachMutation.isPending;

  return (
    <SectionCard
      title="Billing entity"
      description="The company that owns this organization. One billing entity can own several organizations, and they all use the billing entity's subscription tier."
      icon={<Landmark />}
      contentClassName="pt-0"
    >
      <div className="space-y-3" data-testid="admin-organization-billing-entity">
        {billingEntity === undefined ? (
          // Owner unknown. Never offer a move here: re-pointing a live
          // contract requires first showing the current company.
          <div className="space-y-1">
            <SectionLabel>Owner</SectionLabel>
            <PageDescription data-testid="admin-organization-billing-entity-unknown">
              {ownerState.status === 'error'
                ? 'We could not load who owns this organization.'
                : ownerState.status === 'hidden'
                  ? 'Only platform admins can see who owns this organization.'
                  : 'Loading…'}
            </PageDescription>
            {ownerState.status === 'error' && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={onRetryOwner}
                data-testid="admin-organization-billing-entity-retry"
              >
                Try again
              </Button>
            )}
          </div>
        ) : (
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="space-y-1">
              <div className="flex items-center gap-2">
                <SectionLabel>Owned by</SectionLabel>
                <Badge variant="secondary" data-testid="admin-organization-billing-entity-name">
                  {billingEntity.name}
                </Badge>
              </div>
              <PageDescription>
                {siblingSlugs.length > 0
                  ? `${billingEntity.name} also owns ${siblingSlugs.join(', ')}. All of these organizations use the tier set here.`
                  : `${billingEntity.name} owns this organization only. Its tier is set here.`}
              </PageDescription>
            </div>
            {canEdit && (
              <div className="flex flex-col items-stretch gap-2 sm:items-end">
                <Select
                  value={billingEntity.subscriptionTier}
                  onValueChange={(next) =>
                    handleBillingEntityTierSelect(next as OrganizationSubscriptionTier)
                  }
                  disabled={billingEntityTierMutation.isPending}
                >
                  <SelectTrigger
                    className="w-40"
                    aria-label="Billing entity subscription tier"
                    data-testid="admin-organization-billing-entity-tier-select"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ORGANIZATION_SUBSCRIPTION_TIERS.map((tier) => (
                      <SelectItem
                        key={tier}
                        value={tier}
                        data-testid={`admin-organization-billing-entity-tier-option-${tier}`}
                      >
                        {SUBSCRIPTION_TIER_LABELS[tier]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select
                  value={billingEntity.id}
                  onValueChange={(next) => {
                    if (next === billingEntity.id) return;
                    setPendingMove({
                      billingEntityId: next,
                      reason: '',
                      ssoResetRequired: false,
                      acknowledgeSsoReset: false,
                    });
                  }}
                  disabled={busy || billingEntitiesQuery.isLoading}
                >
                  <SelectTrigger
                    className="w-56"
                    aria-label="Move organization to company"
                    data-testid="admin-organization-billing-entity-select"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {(billingEntitiesQuery.data?.billingEntities ?? [billingEntity]).map(
                      (candidate) => (
                        <SelectItem key={candidate.id} value={candidate.id}>
                          {candidate.name}
                        </SelectItem>
                      ),
                    )}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
        )}
        {canEdit && billingEntity && (
          <div className="space-y-2 border-t pt-3" data-testid="admin-company-email-domains">
            <SectionLabel>Company email domains</SectionLabel>
            <PageDescription>
              These domains belong to {billingEntity.name}, not to one organization. Verification
              status and routing overrides are on the{' '}
              <Link
                to={ROUTES.admin.company(billingEntity.id)}
                className="underline underline-offset-2"
                data-testid="admin-company-domain-claims-link"
              >
                company page
              </Link>
              .
            </PageDescription>
            {companyDomainsQuery.isLoading ? (
              <PageDescription>Loading…</PageDescription>
            ) : companyDomainsQuery.isError ? (
              <PageDescription>Could not load email domains.</PageDescription>
            ) : companyDomainsQuery.data?.length ? (
              companyDomainsQuery.data.map((claim) => (
                <div key={claim.id} className="flex items-center gap-2 rounded-md border p-2">
                  <span className="min-w-0 flex-1 truncate font-mono text-sm">{claim.domain}</span>
                  <Select
                    value={billingEntity.id}
                    onValueChange={(next) => {
                      if (next !== billingEntity.id) {
                        setPendingDomainChange({
                          domain: claim.domain,
                          action: 'reassign',
                          billingEntityId: next,
                          reason: '',
                        });
                      }
                    }}
                  >
                    <SelectTrigger className="w-48" aria-label={`Move ${claim.domain} to company`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {(billingEntitiesQuery.data?.billingEntities ?? [billingEntity]).map(
                        (candidate) => (
                          <SelectItem key={candidate.id} value={candidate.id}>
                            {candidate.name}
                          </SelectItem>
                        ),
                      )}
                    </SelectContent>
                  </Select>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon-sm"
                    aria-label={`Remove ${claim.domain}`}
                    onClick={() =>
                      setPendingDomainChange({ domain: claim.domain, action: 'remove', reason: '' })
                    }
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))
            ) : (
              <PageDescription>No email domains claimed.</PageDescription>
            )}
          </div>
        )}
      </div>
      <ConfirmDialog
        open={pendingDomainChange !== null}
        onOpenChange={(open) => {
          if (!open && !removeDomainMutation.isPending && !reassignDomainMutation.isPending) {
            setPendingDomainChange(null);
          }
        }}
        title={
          pendingDomainChange?.action === 'reassign'
            ? 'Move email domain to another company?'
            : 'Remove company email domain?'
        }
        variant={pendingDomainChange?.action === 'remove' ? 'destructive' : 'default'}
        confirmLabel={pendingDomainChange?.action === 'reassign' ? 'Move domain' : 'Remove domain'}
        disabled={
          !pendingDomainChange?.reason.trim() ||
          removeDomainMutation.isPending ||
          reassignDomainMutation.isPending
        }
        onConfirm={() => {
          if (!pendingDomainChange) return;
          if (pendingDomainChange.action === 'reassign' && pendingDomainChange.billingEntityId) {
            reassignDomainMutation.mutate({
              domain: pendingDomainChange.domain,
              billingEntityId: pendingDomainChange.billingEntityId,
              reason: pendingDomainChange.reason,
            });
          } else {
            removeDomainMutation.mutate({
              domain: pendingDomainChange.domain,
              reason: pendingDomainChange.reason,
            });
          }
        }}
        body={
          pendingDomainChange && (
            <div className="space-y-3">
              <PageDescription>
                {pendingDomainChange.action === 'reassign'
                  ? `${pendingDomainChange.domain} will stop routing to ${billingEntity?.name ?? 'this company'}. Its auto-join destination is cleared.`
                  : `${pendingDomainChange.domain} will stop identity-provider discovery and auto-join. Existing members stay put.`}
              </PageDescription>
              <div className="space-y-2">
                <Label htmlFor="admin-company-domain-reason">Reason</Label>
                <Input
                  id="admin-company-domain-reason"
                  value={pendingDomainChange.reason}
                  maxLength={500}
                  onChange={(event) =>
                    setPendingDomainChange({
                      ...pendingDomainChange,
                      reason: event.target.value,
                    })
                  }
                />
              </div>
            </div>
          )
        }
      />
      <ConfirmDialog
        open={pendingMove !== null}
        onOpenChange={(open) => {
          if (!open && !moveInFlight.current) setPendingMove(null);
        }}
        title="Move organization to another company?"
        variant="default"
        confirmLabel={
          pendingMove?.ssoResetRequired ? 'Move and switch SSO off' : 'Move organization'
        }
        disabled={
          !pendingMove ||
          pendingMove.reason.trim().length === 0 ||
          (pendingMove.ssoResetRequired && !pendingMove.acknowledgeSsoReset) ||
          attachMutation.isPending
        }
        onConfirm={() => {
          if (!pendingMove) return;
          moveInFlight.current = true;
          attachMutation.mutate(pendingMove);
        }}
        body={
          pendingMove && (
            <div className="space-y-4" data-testid="admin-organization-billing-entity-move">
              <PageDescription>
                {organization.name} moves to{' '}
                {billingEntitiesQuery.data?.billingEntities.find(
                  (candidate) => candidate.id === pendingMove.billingEntityId,
                )?.name ?? 'the selected company'}
                . It takes that company&apos;s subscription tier and subscription window; the window
                it has today no longer applies. The reason you give is recorded on the company audit
                log.
              </PageDescription>
              <div className="space-y-2">
                <Label htmlFor="admin-organization-billing-entity-move-reason">Reason</Label>
                <Input
                  id="admin-organization-billing-entity-move-reason"
                  value={pendingMove.reason}
                  onChange={(event) =>
                    setPendingMove({ ...pendingMove, reason: event.target.value })
                  }
                  placeholder="Consolidating under the Acme contract"
                  maxLength={500}
                  data-testid="admin-organization-billing-entity-move-reason"
                />
              </div>
              {pendingMove.ssoResetRequired && (
                <div
                  className="flex items-start gap-3 rounded-md border border-warning/40 bg-warning/10 p-3"
                  data-testid="admin-organization-billing-entity-move-sso-reset"
                >
                  <Switch
                    id="admin-organization-billing-entity-move-acknowledge"
                    checked={pendingMove.acknowledgeSsoReset}
                    onCheckedChange={(checked) =>
                      setPendingMove({ ...pendingMove, acknowledgeSsoReset: checked })
                    }
                    aria-label="Acknowledge that single sign-on is switched off"
                    data-testid="admin-organization-billing-entity-move-acknowledge"
                  />
                  <Label
                    htmlFor="admin-organization-billing-entity-move-acknowledge"
                    className="font-normal leading-snug"
                  >
                    This organization requires single sign-on through its current company, and the
                    new company trusts a different identity provider. Moving it switches single
                    sign-on off, and members sign in with their other methods until it is set up
                    again. I understand.
                  </Label>
                </div>
              )}
            </div>
          )
        }
      />
      <ConfirmDialog
        open={pendingBillingEntityTier !== null}
        onOpenChange={(open) => {
          if (!open) setPendingBillingEntityTier(null);
        }}
        title="Change subscription tier?"
        // The status read covers this org only, and the billing entity may own several,
        // so we cannot list what each one has set up. Say what the tier does,
        // not that specific features are definitely running today.
        description={
          <>
            {billingEntity?.name ?? 'This billing entity'} owns{' '}
            {ownedOrganizations.length === 1
              ? 'this organization'
              : `${ownedOrganizations.length} organizations`}
            , and they all move to the{' '}
            {pendingBillingEntityTier ? SUBSCRIPTION_TIER_LABELS[pendingBillingEntityTier] : ''}{' '}
            tier, which does not include enterprise identity. Any single sign-on or directory sync{' '}
            {ownedOrganizations.length === 1 ? 'it has' : 'they have'} set up stops working shortly.
            Those settings are kept, so they start working again if you switch back to Enterprise.
          </>
        }
        confirmLabel="Change tier"
        onConfirm={() => {
          if (pendingBillingEntityTier) billingEntityTierMutation.mutate(pendingBillingEntityTier);
        }}
      />
    </SectionCard>
  );
}

interface SubscriptionTierSectionProps {
  organization: Organization;
  /**
   * The company governing this org's tier, `undefined` when we do not know
   * yet. Every organization belongs to exactly one company
   * (`organizations.billing_entity_id` is `NOT NULL`), so the tier is always
   * the company's to set — this card only reports it.
   */
  billingEntity: BillingEntity | undefined;
  /** Platform admins see who governs the tier; everyone else sees the value. */
  canEdit: boolean;
}

/**
 * Read-only card reporting the org's effective subscription tier. The tier is
 * the entitlement record for tier features (SSO, SCIM, Okta sync, ID-JAG), and
 * it is owned by the company above the org, so the picker and its
 * downgrade confirmation live on the Company card. A write to
 * `PATCH /admin/organizations/:id/subscription-tier` would be refused with a
 * 409 while a company governs, which is always.
 */
function SubscriptionTierSection({
  organization,
  billingEntity,
  canEdit,
}: SubscriptionTierSectionProps) {
  const subscriptionTier = organization.subscriptionTier ?? DEFAULT_ORGANIZATION_SUBSCRIPTION_TIER;

  // A viewer who cannot edit also cannot read the owner, so say nothing about
  // companies to them: show the org's own tier, which rides the organization
  // resource they can already see.
  if (!canEdit) {
    return (
      <SectionCard
        title="Subscription tier"
        description={SUBSCRIPTION_TIER_CARD_DESCRIPTION}
        icon={<Tag />}
        contentClassName="pt-0"
      >
        <div
          className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between"
          data-testid="admin-organization-subscription-tier"
        >
          <SectionLabel>Tier</SectionLabel>
          <Badge variant="secondary" data-testid="admin-organization-subscription-tier-badge">
            {SUBSCRIPTION_TIER_LABELS[subscriptionTier]}
          </Badge>
        </div>
      </SectionCard>
    );
  }

  return (
    <SectionCard
      title="Subscription tier"
      description={SUBSCRIPTION_TIER_CARD_DESCRIPTION}
      icon={<Tag />}
      contentClassName="pt-0"
    >
      <div className="space-y-1" data-testid="admin-organization-subscription-tier">
        <SectionLabel>Tier</SectionLabel>
        {billingEntity ? (
          <>
            <Badge variant="secondary" data-testid="admin-organization-subscription-tier-badge">
              {
                SUBSCRIPTION_TIER_LABELS[
                  effectiveSubscriptionTier(billingEntity.subscriptionTier, subscriptionTier)
                ]
              }
            </Badge>
            <PageDescription data-testid="admin-organization-subscription-tier-governed">
              Set by {billingEntity.name}, the company that owns this organization. Change it on the
              Company card above.
            </PageDescription>
          </>
        ) : (
          <PageDescription data-testid="admin-organization-subscription-tier-unknown">
            We could not tell which company sets this organization&apos;s tier. Try the Company card
            above.
          </PageDescription>
        )}
      </div>
    </SectionCard>
  );
}

/**
 * Read-only enterprise identity status. Entitlement follows the subscription
 * tier (the Enterprise tier includes it), so there is no toggle here:
 * operators change the tier above, and this card shows what that change
 * affects: whether access is currently included and which features the org
 * has configured.
 */
function EnterpriseIdentitySection({ organization }: { organization: Organization }) {
  const statusQuery = useQuery({
    queryKey: queryKeys.adminOrganizationEnterpriseIdentity(organization.id),
    queryFn: () => api.getAdminEnterpriseIdentityStatus(organization.id),
  });
  // The company tier is authoritative. Fail closed until the server status
  // read answers rather than consulting the org compatibility field.
  const enabled = statusQuery.data?.entitled ?? false;
  const configuredFeatures = configuredFeatureLabels(statusQuery.data);

  return (
    <SectionCard
      title="Enterprise identity"
      description="Single sign-on, directory sync, and identity policies the organization runs itself. Included on the Enterprise tier, and organization admins set up each feature from there."
      icon={<ShieldCheck />}
      contentClassName="pt-0"
    >
      <div
        className="flex items-center justify-between gap-4"
        data-testid="admin-organization-enterprise-identity"
      >
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <SectionLabel>Product access</SectionLabel>
            <Badge
              variant={enabled ? 'default' : 'outline'}
              data-testid="admin-organization-enterprise-identity-badge"
            >
              {enabled ? 'Included' : 'Not included'}
            </Badge>
          </div>
          <PageDescription>
            {configuredFeatures.length > 0
              ? `Set up: ${configuredFeatures.join(', ')}.${
                  enabled
                    ? ''
                    : ' These are not working on the current tier. Their settings are kept and start working again on Enterprise.'
                }`
              : statusQuery.isError
                ? 'We could not load what this organization has set up.'
                : enabled
                  ? 'Organization admins can set this up. No identity provider yet.'
                  : 'Not available on the current tier. Nobody can set it up or sign in through it.'}
          </PageDescription>
          {statusQuery.isError && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => void statusQuery.refetch()}
            >
              Try again
            </Button>
          )}
        </div>
      </div>
    </SectionCard>
  );
}

interface TeamsSectionProps {
  teams: Team[] | undefined;
  isLoading: boolean;
  isError: boolean;
  orgSlug: string;
}

function TeamsSection({ teams, isLoading, isError, orgSlug }: TeamsSectionProps) {
  const count = teams?.length ?? 0;
  const columns = buildTeamColumns(orgSlug);
  return (
    <SectionCard
      title="Teams"
      description={isLoading ? 'Loading…' : `${count} team${count !== 1 ? 's' : ''}`}
      icon={<UsersRound />}
      contentClassName="pt-0"
    >
      <AsyncContent isLoading={isLoading} isError={isError} errorMessage="Failed to load teams.">
        {count === 0 ? (
          <EmptyState
            variant="bordered"
            icon={<UsersRound className="h-8 w-8" />}
            title="No teams yet"
            description="This organization does not have any teams."
          />
        ) : (
          <DataTable<Team> data={teams!} columns={columns} rowKey={(team) => team.id} />
        )}
      </AsyncContent>
    </SectionCard>
  );
}

interface OrganizationStatusSectionProps {
  organization: Organization;
  /** Whether the current viewer can mutate org-status state. */
  viewerCanManage: boolean;
}

/**
 * Card that toggles `organizations.status`. A disabled org is rejected
 * by the auth middleware with HTTP 403 `organization_disabled` on any
 * request whose active org resolves to it, and the web shell renders
 * an `OrganizationDisabledScreen` (with an org-switcher for members
 * who have other active orgs) instead of mounting the org pages.
 */
function OrganizationStatusSection({
  organization,
  viewerCanManage,
}: OrganizationStatusSectionProps) {
  const queryClient = useQueryClient();
  const [pending, setPending] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const isDisabled = organization.status === 'disabled';

  const toggle = useMutation({
    mutationFn: (next: AccountStatus) => api.setOrganizationStatus(organization.id, next),
    onSuccess: (_, next) => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.adminOrganization(organization.id),
      });
      void queryClient.invalidateQueries({ queryKey: queryKeys.adminOrganizations });
      toast.success(next === 'disabled' ? 'Organization disabled' : 'Organization re-enabled');
    },
    onError: (err: unknown) => {
      const message = err instanceof Error ? err.message : 'Failed to update organization status';
      toast.error(message);
    },
    onSettled: () => setPending(false),
  });

  return (
    <SectionCard
      title="Organization status"
      description="Disabled organizations are blocked at the API. Members with other organizations are offered a switcher; members whose only org is this one see a contact-support screen."
      icon={<Ban />}
      variant={isDisabled ? 'destructive' : undefined}
      contentClassName="space-y-4"
    >
      <div className="flex flex-col gap-3 rounded-lg border p-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-medium">Disabled</span>
            {isDisabled ? (
              <Badge variant="destructive" className="gap-1 text-3xs">
                <Ban className="h-3 w-3" />
                Yes
              </Badge>
            ) : (
              <Badge variant="outline" className="text-3xs text-muted-foreground">
                No
              </Badge>
            )}
          </div>
          <p className="mt-0.5 text-xs text-muted-foreground">
            Toggle off to restore access. Member sessions are preserved, so anyone in this org can
            keep using their other orgs in the meantime.
          </p>
        </div>
        <Switch
          data-testid="organization-disabled-toggle"
          aria-label="Toggle organization disabled"
          disabled={pending || !viewerCanManage}
          checked={isDisabled}
          onCheckedChange={(next) => {
            if (next) {
              setConfirmOpen(true);
            } else {
              setPending(true);
              toggle.mutate('active');
            }
          }}
        />
      </div>

      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title="Disable this organization?"
        description={
          <>
            Members will see a switcher to their other organizations (or a contact-support message
            if this is their only one). All org-scoped API calls will return 403 until you re-enable
            it. No data is deleted.
          </>
        }
        confirmLabel="Disable organization"
        onConfirm={() => {
          setPending(true);
          toggle.mutate('disabled');
        }}
      />
    </SectionCard>
  );
}

interface SubscriptionSectionProps {
  organization: Organization;
  /** Whether the current viewer can mutate subscription state (platform admin). */
  viewerCanManage: boolean;
}

/**
 * Card that manages an org's subscription via
 * `PUT /admin/organizations/:id/subscription`. Access is computed from the
 * subscription window: once it lapses, the org's `disabledReason` resolves to
 * `'subscription_expired'` and members see the `TrialExpiredScreen` — no cron
 * or stored status flip. This card is the admin lever — start a subscription
 * on a standard org, extend the window to N days from now, expire it
 * immediately, or clear it to make the org standard (unlimited) again.
 */
function SubscriptionSection({ organization, viewerCanManage }: SubscriptionSectionProps) {
  const queryClient = useQueryClient();
  const [days, setDays] = useState(14);
  // `subscription` is present for any org that holds one, including one whose
  // window has already lapsed. `ended` is the lapsed-window state, surfaced by
  // the computed `disabledReason` or directly from the window.
  const hasSubscription = organization.subscription != null;
  const ended =
    organization.disabledReason === 'subscription_expired' ||
    (organization.subscription != null && !isSubscriptionActive(organization.subscription));

  const mutation = useMutation({
    mutationFn: (subscription: { type?: 'trial'; durationDays?: number } | null) =>
      api.setOrganizationSubscription(organization.id, subscription),
    onSuccess: (_, vars) => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.adminOrganization(organization.id),
      });
      void queryClient.invalidateQueries({ queryKey: queryKeys.adminOrganizations });
      toast.success(vars ? 'Subscription updated' : 'Subscription cleared');
    },
    onError: (err) =>
      toast.error('Failed to update subscription', {
        description: err instanceof Error ? err.message : undefined,
      }),
  });

  const expireMutation = useMutation({
    mutationFn: () => api.expireOrganizationSubscription(organization.id),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.adminOrganization(organization.id),
      });
      void queryClient.invalidateQueries({ queryKey: queryKeys.adminOrganizations });
      toast.success('Subscription expired');
    },
    onError: (err) =>
      toast.error('Failed to expire subscription', {
        description: err instanceof Error ? err.message : undefined,
      }),
  });
  const busy = mutation.isPending || expireMutation.isPending;

  return (
    <SectionCard
      title="Subscription"
      description="A subscription grants time-limited access. When the window ends, the org is blocked until you extend the subscription or clear it to make the org standard."
      icon={<Clock />}
      variant={ended ? 'destructive' : undefined}
      contentClassName="space-y-4"
    >
      <div
        className="flex flex-col gap-3 rounded-lg border p-3"
        data-testid="admin-organization-subscription"
      >
        <div className="flex items-center gap-2">
          <span className="font-medium">Subscription</span>
          {!hasSubscription ? (
            <Badge
              variant="outline"
              className="text-3xs text-muted-foreground"
              data-testid="admin-organization-subscription-status"
            >
              None
            </Badge>
          ) : ended ? (
            <Badge
              variant="destructive"
              className="gap-1 text-3xs"
              data-testid="admin-organization-subscription-status"
            >
              <Clock className="h-3 w-3" />
              Ended
            </Badge>
          ) : (
            <Badge
              variant="secondary"
              className="gap-1 text-3xs"
              data-testid="admin-organization-subscription-status"
            >
              <Clock className="h-3 w-3" />
              Active
            </Badge>
          )}
        </div>
        {organization.subscription && (
          <dl className="grid grid-cols-1 gap-x-6 gap-y-2 text-xs text-muted-foreground sm:grid-cols-2">
            <div>
              <dt className="font-medium text-foreground">Started</dt>
              <dd className="mt-0.5">{formatDateLong(organization.subscription.startsAt)}</dd>
            </div>
            <div>
              <dt className="font-medium text-foreground">{ended ? 'Ended' : 'Ends'}</dt>
              <dd className="mt-0.5">
                {`${formatDateLong(organization.subscription.endsAt)} (${formatRelativeTime(
                  organization.subscription.endsAt,
                )})`}
              </dd>
            </div>
          </dl>
        )}
      </div>

      {viewerCanManage && (
        <div className="flex flex-wrap items-end gap-2">
          <div className="space-y-1">
            <Label htmlFor="admin-subscription-days" className="text-xs">
              Subscription length (days)
            </Label>
            <Input
              id="admin-subscription-days"
              type="number"
              min={1}
              max={365}
              value={days}
              onChange={(e) => setDays(Number(e.target.value))}
              className="w-28"
              data-testid="admin-organization-subscription-days"
            />
          </div>
          <Button
            type="button"
            onClick={() => mutation.mutate({ type: 'trial', durationDays: days })}
            disabled={busy || !Number.isFinite(days) || days < 1}
            data-testid="admin-organization-subscription-set"
          >
            {hasSubscription ? 'Extend subscription' : 'Start subscription'}
          </Button>
          {hasSubscription && !ended && (
            <Button
              type="button"
              variant="destructive"
              onClick={() => expireMutation.mutate()}
              disabled={busy}
              data-testid="admin-organization-subscription-expire"
            >
              Expire now
            </Button>
          )}
          {hasSubscription && (
            <Button
              type="button"
              variant="outline"
              onClick={() => mutation.mutate(null)}
              disabled={busy}
              data-testid="admin-organization-subscription-clear"
            >
              Clear subscription
            </Button>
          )}
        </div>
      )}
    </SectionCard>
  );
}

interface RebuildBaseBlueprintSectionProps {
  organizationId: string;
}

function RebuildBaseBlueprintSection({ organizationId }: RebuildBaseBlueprintSectionProps) {
  const rebuildMutation = useMutation({
    mutationFn: () => api.rebuildBaseImage(organizationId),
    onSuccess: () => {
      toast.success('Base Blueprint rebuild started', {
        description: 'The rebuild runs in the background and may take several minutes.',
      });
    },
    onError: (err) => {
      toast.error('Failed to start base Blueprint rebuild', {
        description: err instanceof Error ? err.message : undefined,
      });
    },
  });

  return (
    <SectionCard
      title="Base Blueprint"
      description="Force a fresh rebuild of this organization's base devbox image. The rebuild runs even when nothing changed, then cascades to child Blueprints."
      icon={<RefreshCw />}
    >
      <ConfirmDialog
        title="Rebuild base Blueprint"
        description={
          <>
            Force-rebuild this organization's base Blueprint and cascade to every child Blueprint?
            This consumes Runloop build minutes and can take several minutes to complete.
          </>
        }
        confirmLabel="Rebuild"
        onConfirm={() => rebuildMutation.mutate()}
        disabled={rebuildMutation.isPending}
        trigger={
          <Button
            variant="outline"
            data-testid="admin-organization-rebuild-base-blueprint"
            aria-label="Rebuild base Blueprint"
            disabled={rebuildMutation.isPending}
          >
            <RefreshCw className="icon-sm" />
            Rebuild base Blueprint
          </Button>
        }
      />
    </SectionCard>
  );
}

interface DeleteOrganizationSectionProps {
  organization: Organization;
}

/**
 * Destructive "Danger zone" card that hard-deletes the organization and
 * every row that references it (members, teams, roles, agents, secrets,
 * and all plugin data) via `DELETE /admin/organizations/:id`. On success
 * we leave the now-stale detail page and return to the org list.
 */
function DeleteOrganizationSection({ organization }: DeleteOrganizationSectionProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const deleteMutation = useMutation({
    mutationFn: () => api.deleteOrganization(organization.id),
    onSuccess: () => {
      toast.success('Organization deleted');
      void queryClient.invalidateQueries({ queryKey: queryKeys.adminOrganizations });
      navigate(ROUTES.admin.organizations);
    },
    onError: (err) => {
      toast.error('Failed to delete organization', {
        description: err instanceof Error ? err.message : undefined,
      });
    },
  });

  return (
    <SectionCard
      title="Danger zone"
      description="Permanently delete this organization and all of its data. This action cannot be undone."
      icon={<Trash2 />}
      variant="destructive"
    >
      <ConfirmDialog
        title="Delete this organization?"
        description={
          <>
            Permanently delete <strong>{organization.name}</strong> and every row that references
            it: members, teams, roles, agents, secrets, and all plugin data. This cannot be undone.
          </>
        }
        confirmLabel="Delete organization"
        onConfirm={() => deleteMutation.mutate()}
        disabled={deleteMutation.isPending}
        trigger={
          <Button
            variant="destructive"
            data-testid="admin-organization-delete"
            aria-label="Delete organization"
            disabled={deleteMutation.isPending}
          >
            <Trash2 className="icon-sm" />
            Delete organization
          </Button>
        }
      />
    </SectionCard>
  );
}

/**
 * Left-border accent per metric so the KPI row reads as one system while still
 * differentiating each signal. Unknown keys fall back to a neutral accent.
 */
const ACTIVITY_ACCENTS: Record<string, StatGridItem['accent']> = {
  'core.agent_sessions': 'violet',
  'core.users_active': 'cyan',
  'personas.created': 'teal',
  'flows.created': 'indigo',
  'flows.runs': 'purple',
};

/**
 * At-a-glance "is this org actually using the product?" panel. Coarse counts
 * over the last ~7 days (agent sessions, sign-ins, personas, flows) so an
 * operator can spot a freshly onboarded org that never got going and follow up.
 */
function RecentActivitySection({ organizationId }: { organizationId: string }) {
  const activityQuery = useQuery({
    queryKey: queryKeys.adminOrganizationActivity(organizationId),
    queryFn: () => api.getOrganizationActivity(organizationId),
    enabled: organizationId.length > 0,
  });

  const summary = activityQuery.data;
  const metrics = summary?.metrics ?? [];
  const windowDays = summary?.windowDays ?? 7;
  const hasActivity = metrics.some((metric) => metric.count > 0);
  const items: StatGridItem[] = metrics.map((metric) => ({
    label: metric.label,
    value: metric.count.toLocaleString(),
    description: metric.description,
    accent: ACTIVITY_ACCENTS[metric.key] ?? 'neutral',
  }));

  return (
    <SectionCard
      title="Recent activity"
      description={`Past ${windowDays} days`}
      icon={<Activity />}
      data-testid="admin-organization-activity"
    >
      <AsyncContent
        isLoading={activityQuery.isLoading}
        isError={activityQuery.isError}
        errorMessage="Failed to load recent activity."
      >
        {items.length === 0 ? (
          <EmptyState
            variant="bordered"
            icon={<Activity className="h-8 w-8" />}
            title="No activity signals"
            description="Activity metrics are unavailable for this organization."
          />
        ) : (
          <div className="space-y-3">
            <StatGrid items={items} />
            {!hasActivity && (
              <PageDescription className="text-xs">
                No activity in the last {windowDays} days. This organization may need a nudge to get
                started.
              </PageDescription>
            )}
          </div>
        )}
      </AsyncContent>
    </SectionCard>
  );
}

export function AdminOrganizationDetailPage() {
  const { organizationId } = useParams<{ organizationId: string }>();
  const id = organizationId ?? '';
  const queryClient = useQueryClient();
  const auth = useAuth();

  const organizationQuery = useQuery({
    queryKey: queryKeys.adminOrganization(id),
    queryFn: () => api.getOrganization(id),
    enabled: id.length > 0,
  });
  // The org's commercial owner. Platform-admin only and deliberately its own
  // read: the organization resource carries no billing entity, so this is the single
  // place the Billing entity, Subscription tier, and Enterprise identity cards learn
  // whether a billing entity governs this org.
  const billingEntityQuery = useQuery({
    queryKey: queryKeys.adminOrganizationBillingEntity(id),
    queryFn: () => api.getAdminOrganizationBillingEntity(id),
    enabled: id.length > 0 && auth.isPlatformAdmin,
  });
  const ownerState: OwnerState = !auth.isPlatformAdmin
    ? { status: 'hidden' }
    : billingEntityQuery.data
      ? { status: 'ready', owner: billingEntityQuery.data }
      : billingEntityQuery.isError
        ? { status: 'error' }
        : { status: 'loading' };
  const billingEntity = governingBillingEntity(ownerState);

  const membersQuery = useQuery({
    queryKey: queryKeys.adminOrganizationMembers(id),
    queryFn: () => api.listOrganizationMembers(id),
    enabled: id.length > 0,
  });
  const teamsQuery = useQuery({
    queryKey: queryKeys.adminOrganizationTeams(id),
    queryFn: () => api.listTeams(id),
    enabled: id.length > 0,
  });
  // Member entries only carry `userId`; pull the full user list once so we can
  // render avatars and display names. Reuses the same cache key as the admin
  // users page so navigating back and forth is instant.
  const usersQuery = useQuery({
    queryKey: queryKeys.adminUsers,
    queryFn: () => api.listUsers(),
  });
  // Available roles for the inline role editor. Only org-scoped roles can be
  // bound to a member; team-scoped roles are filtered below.
  const rolesQuery = useQuery({
    queryKey: ['admin', 'organization', id, 'roles'] as const,
    queryFn: () => api.listOrganizationRoles(id),
    enabled: id.length > 0,
  });

  // This panel acts on an arbitrary tenant, so a blanket session re-read
  // would fire `/auth/me` on every admin action against an org the caller
  // has nothing to do with. `useRefreshSessionIfSelf` re-reads only when the
  // target is the caller — the one case where their own permissions changed.
  const refreshIfSelf = useRefreshSessionIfSelf();

  const addMemberMutation = useMutation({
    mutationFn: (userId: string) => api.addOrganizationMember(id, userId),
    onSuccess: (_data, userId) => {
      toast.success('Member added');
      void queryClient.invalidateQueries({ queryKey: queryKeys.adminOrganizationMembers(id) });
      refreshIfSelf(userId);
    },
    onError: (err) => {
      toast.error('Failed to add member', {
        description: err instanceof Error ? err.message : undefined,
      });
    },
  });

  const assignRoleMutation = useMutation({
    mutationFn: ({ userId, roleId }: { userId: string; roleId: string }) =>
      api.assignOrganizationRole(id, userId, roleId),
    onSuccess: (_data, { userId }) => {
      toast.success('Role assigned');
      void queryClient.invalidateQueries({ queryKey: queryKeys.adminOrganizationMembers(id) });
      refreshIfSelf(userId);
    },
    onError: (err) => {
      toast.error('Failed to assign role', {
        description: err instanceof Error ? err.message : undefined,
      });
    },
  });

  const revokeRoleMutation = useMutation({
    mutationFn: ({ userId, roleId }: { userId: string; roleId: string }) =>
      api.revokeOrganizationRole(id, userId, roleId),
    onSuccess: (_data, { userId }) => {
      toast.success('Role revoked');
      void queryClient.invalidateQueries({ queryKey: queryKeys.adminOrganizationMembers(id) });
      refreshIfSelf(userId);
    },
    onError: (err) => {
      toast.error('Failed to revoke role', {
        description: err instanceof Error ? err.message : undefined,
      });
    },
  });

  const removeMemberMutation = useMutation({
    mutationFn: (userId: string) => api.removeOrganizationMember(id, userId),
    onSuccess: (_data, userId) => {
      toast.success('Member removed');
      void queryClient.invalidateQueries({ queryKey: queryKeys.adminOrganizationMembers(id) });
      refreshIfSelf(userId);
    },
    onError: (err) => {
      toast.error('Failed to remove member', {
        description: err instanceof Error ? err.message : undefined,
      });
    },
  });

  const backAction = (
    <Button asChild variant="outline" size="sm">
      <Link to={ROUTES.admin.organizations}>Back to organizations</Link>
    </Button>
  );

  const navSections = [
    { id: 'identity', label: 'Identity', icon: Building2 },
    { id: 'recent-activity', label: 'Recent Activity', icon: Activity },
    { id: 'organization-status', label: 'Organization Status', icon: Ban },
    { id: 'subscription', label: 'Subscription', icon: Clock },
    { id: 'billing-entity', label: 'Billing entity', icon: Landmark },
    { id: 'subscription-tier', label: 'Subscription Tier', icon: Tag },
    { id: 'enterprise-identity', label: 'Enterprise Identity', icon: ShieldCheck },
    { id: 'capabilities', label: 'Capabilities', icon: KeyRound },
    { id: 'members', label: 'Members', icon: Users },
    { id: 'invites', label: 'Invites', icon: Mail },
    { id: 'teams', label: 'Teams', icon: UsersRound },
    { id: 'base-blueprint', label: 'Base Blueprint', icon: RefreshCw },
    { id: 'danger-zone', label: 'Danger Zone', icon: Trash2 },
  ];

  if (organizationQuery.isLoading) {
    return (
      <AdminSubPageLayout title="Organization" actions={backAction}>
        <LoadingState />
      </AdminSubPageLayout>
    );
  }

  if (organizationQuery.isError || !organizationQuery.data) {
    return (
      <AdminSubPageLayout title="Organization" actions={backAction}>
        <EmptyState
          variant="bordered"
          icon={<Building2 className="h-8 w-8" />}
          title="Organization not found"
          description="This organization may have been removed or you do not have access."
          action={
            <Button asChild variant="link">
              <Link to={ROUTES.admin.organizations}>Back to organizations</Link>
            </Button>
          }
        />
      </AdminSubPageLayout>
    );
  }

  const organization = organizationQuery.data;

  const usersById = new Map<string, User>();
  for (const user of usersQuery.data ?? []) {
    usersById.set(user.id, user);
  }
  const memberRows: MemberRow[] | undefined = membersQuery.data?.map((m) => ({
    userId: m.userId,
    memberId: m.memberId,
    roles: m.roles,
    user: usersById.get(m.userId),
  }));
  const memberUserIds = new Set(membersQuery.data?.map((m) => m.userId) ?? []);
  const addableUsers = (usersQuery.data ?? []).filter((u) => !memberUserIds.has(u.id));
  const creator = organization.createdByUserId
    ? usersById.get(organization.createdByUserId)
    : undefined;
  const orgScopedRoles: Role[] = (rolesQuery.data ?? []).filter((r) => r.scope === 'org');
  const rolesPending = assignRoleMutation.isPending || revokeRoleMutation.isPending;

  return (
    <AdminSubPageLayout
      title={organization.name}
      description={organization.slug}
      actions={backAction}
    >
      <div className="flex w-full gap-10">
        <div className="min-w-0 flex-1 space-y-6">
          <div id="identity" className="scroll-mt-24">
            <IdentitySection organization={organization} creator={creator} />
          </div>
          <div id="recent-activity" className="scroll-mt-24">
            <RecentActivitySection organizationId={organization.id} />
          </div>
          <div id="organization-status" className="scroll-mt-24">
            <OrganizationStatusSection
              organization={organization}
              viewerCanManage={auth.isPlatformAdmin}
            />
          </div>
          <div id="subscription" className="scroll-mt-24">
            <SubscriptionSection
              organization={organization}
              viewerCanManage={auth.isPlatformAdmin}
            />
          </div>
          <div id="billing-entity" className="scroll-mt-24">
            <BillingEntitySection
              organization={organization}
              ownerState={ownerState}
              onRetryOwner={() => void billingEntityQuery.refetch()}
              canEdit={auth.isPlatformAdmin}
            />
          </div>
          <div id="subscription-tier" className="scroll-mt-24">
            <SubscriptionTierSection
              organization={organization}
              billingEntity={billingEntity}
              canEdit={auth.isPlatformAdmin}
            />
          </div>
          <div id="enterprise-identity" className="scroll-mt-24">
            <EnterpriseIdentitySection organization={organization} />
          </div>
          <div id="capabilities" className="scroll-mt-24">
            <CapabilitiesSection organization={organization} canEdit={auth.isPlatformAdmin} />
          </div>
          <div id="members" className="scroll-mt-24">
            <MembersSection
              members={memberRows}
              isLoading={membersQuery.isLoading || usersQuery.isLoading}
              isError={membersQuery.isError}
              addableUsers={addableUsers}
              onAddMember={(userId) => addMemberMutation.mutate(userId)}
              isAdding={addMemberMutation.isPending}
              availableRoles={orgScopedRoles}
              rolesPending={rolesPending}
              removePending={removeMemberMutation.isPending}
              onToggleRole={(userId, roleId, next) => {
                if (next) {
                  assignRoleMutation.mutate({ userId, roleId });
                } else {
                  revokeRoleMutation.mutate({ userId, roleId });
                }
              }}
              onRemove={(row) => removeMemberMutation.mutate(row.userId)}
            />
          </div>
          <div id="invites" className="scroll-mt-24">
            <InvitesSection organizationId={organization.id} />
          </div>
          <div id="teams" className="scroll-mt-24">
            <TeamsSection
              teams={teamsQuery.data}
              isLoading={teamsQuery.isLoading}
              isError={teamsQuery.isError}
              orgSlug={organization.slug}
            />
          </div>
          <div id="base-blueprint" className="scroll-mt-24">
            <RebuildBaseBlueprintSection organizationId={organization.id} />
          </div>
          <div id="danger-zone" className="scroll-mt-24">
            <DeleteOrganizationSection organization={organization} />
          </div>
        </div>
        <aside className="hidden w-44 shrink-0 xl:block">
          <div className="sticky top-32">
            <SectionNav sections={navSections} data-testid="admin-org-section-nav" />
          </div>
        </aside>
      </div>
    </AdminSubPageLayout>
  );
}
